import { Box, Divider, Grid, Icon, IconButton, LinearProgress, Popover, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppThemeContext } from '../../../contexts';
import { useUserContext } from '../../../contexts';
import { dateFormat } from '../../../helpers/dateFormat';
import { Button } from '../../MUI/button/Button';
import { EntitySelector } from '../../MUI/entity-selector/EntitySelector';
import { EmployeeAvatarSelector, IEmployeeAvatar } from '../employeeAvatarSelector/EmployeeAvatarSelector';
import { DayView } from './components/DayView';
import { WeekView } from './components/WeekView';
import { MonthView } from './components/MonthView';
import { SchedulerCreateAppointmentModal } from './SchedulerCreateAppointmentModal';
import { SchedulerCreateBlockerModal } from './SchedulerCreateBlockerModal';

interface IProps {
  filterLabel: string;
  filterOptions: { id: number, name: string }[];
  filterOptionType: number;
  businessId?: number;
  startTime: number;
  endTime: number;
  refetch: () => any;
  employees?: IEmployeeAvatar[];
}

export const SchedulerFilterOptionsEnum = {
  Business: 1,
  Employee: 2
} as const;

export const SchedulerViewEnum = {
  Dia: 1,
  Semana: 2,
  Mês: 3
} as const;

export const getSchedulerViewOptions = () => {
  return Object.keys(SchedulerViewEnum).map((key) => ({
    id: SchedulerViewEnum[key as keyof typeof SchedulerViewEnum],
    description: key,
  }));
};

const dayView = getSchedulerViewOptions().find(x => x.id === SchedulerViewEnum.Dia);

export const Scheduler: React.FC<IProps> = ({
  filterLabel,
  filterOptions,
  filterOptionType,
  businessId,
  startTime,
  endTime,
  refetch,
  employees
}) => {
  const [view, setView] = useState<any>(dayView);
  const [option, setOption] = useState<{ id: number, name: string }>();
  const [date, setDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [anchorElProfilePopoverNew, setAnchorElProfilePopoverNew] = useState<null | HTMLElement>(null);

  /** 'appointment' | 'walkin' | 'blocker' | null */
  const [modalType, setModalType] = useState<string | null>(null);

  const isPopoverNewOpen = Boolean(anchorElProfilePopoverNew);
  const { lgDown } = useAppThemeContext();
  const queryClient = useQueryClient();

  // Wrap the parent's refetch to also invalidate appointments and blockers queries
  // used by DayView/WeekView/MonthView, so newly created items appear immediately.
  const handleCreateRefetch = useCallback(() => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['blockers'] });
  }, [refetch, queryClient]);

  // Auto-set to DayView on mobile — WeekView/MonthView are hidden there
  useEffect(() => {
    if (lgDown && view.id !== SchedulerViewEnum.Dia) {
      setView(dayView);
    }
  }, [lgDown]);

  const { validateConfirmedEmail } = useUserContext();

  // ── Resolve o businessId/employeeId a partir do filtro ───────────

  const resolvedBusinessId = (filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId) || 0;
  const resolvedBusinessEmployeeId = filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined;

  // ── Navegação de data ────────────────────────────────────────────

  const daysToChange = useMemo(() => {
    if (view.id === SchedulerViewEnum.Dia) return 1;
    if (view.id === SchedulerViewEnum.Semana) return 7;
    return 0;
  }, [view]);

  const handleAdvanceButtonClick = () => {
    const newDate = new Date(date);
    if (view.id === SchedulerViewEnum.Mês) {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + daysToChange);
    }
    setDate(newDate);
  };

  const handleBackButtonClick = () => {
    const newDate = new Date(date);
    if (view.id === SchedulerViewEnum.Mês) {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - daysToChange);
    }
    setDate(newDate);
  };

  const uniqueOption = filterOptions.length === 1;

  useEffect(() => {
    if (uniqueOption) {
      setOption(filterOptions[0]);
    }
  }, [uniqueOption, filterOptions]);

  const dateView = useMemo(() => {
    if (view.id === SchedulerViewEnum.Dia) {
      return dateFormat(date, 'LL');
    }

    if (view.id === SchedulerViewEnum.Semana) {
      const formattedDate = dateFormat(date, 'MMMM [de] YYYY');
      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    }

    if (view.id === SchedulerViewEnum.Mês) {
      const formattedDate = dateFormat(date, 'MMMM [de] YYYY');
      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    }
  }, [view, date]);

  const generateTimes = () => {
    const hours = [];
    for (let hour = startTime; hour < endTime; hour++) {
      const hourString = hour.toString().padStart(2, '0');

      const formattedHour = hourString + ':00';
      hours.push(formattedHour);

      const halfHourFormatted = hourString + ':30';
      hours.push(halfHourFormatted);
    }
    return hours;
  };

  const handleClosePopover = useCallback(() => {
    setAnchorElProfilePopoverNew(null);
  }, []);

  // ── Handlers dos botões do Popover ───────────────────────────────

  const onClickSchedule = () => {
    if (!validateConfirmedEmail()) return;
    if (!resolvedBusinessId) return;
    handleClosePopover();
    setModalType('appointment');
  };

  const onClickWalkIn = () => {
    if (!validateConfirmedEmail()) return;
    if (!resolvedBusinessId) return;
    handleClosePopover();
    setModalType('walkin');
  };

  const onClickBlocker = () => {
    if (!validateConfirmedEmail()) return;
    if (!resolvedBusinessId) return;
    handleClosePopover();
    setModalType('blocker');
  };

  const handleCloseModal = useCallback(() => {
    setModalType(null);
  }, []);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', pt: 1 }}>
      <Grid container spacing={2} paddingBottom={2}>
        {!lgDown && (
          <Grid item xs={12} lg={3} sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <ToggleButtonGroup
                value={view?.id}
                exclusive
                onChange={(_, value) => {
                  if (value) {
                    const newView = getSchedulerViewOptions().find(v => v.id === value);
                    if (newView) setView(newView);
                  }
                }}
                size='small'
                fullWidth
              >
                {getSchedulerViewOptions().map(opt => (
                  <ToggleButton key={opt.id} value={opt.id}>
                    {opt.description}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} lg={3} sx={{ minWidth: 200, flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!lgDown && <Divider orientation='vertical' flexItem />}
          {filterOptionType === SchedulerFilterOptionsEnum.Employee && employees ? (
            lgDown ? (
              <EmployeeAvatarSelector
                employees={employees}
                value={option?.id}
                onChange={(id) => setOption(id ? filterOptions.find(o => o.id === id) : undefined)}
                disabled={uniqueOption}
                size='small'
                compact
                justifyContent='center'
              />
            ) : (
              <EmployeeAvatarSelector
                employees={employees}
                value={option?.id}
                onChange={(id) => setOption(id ? filterOptions.find(o => o.id === id) : undefined)}
                disabled={uniqueOption}
                size='small'
                compact
                scrollable
              />
            )
          ) : (
            <EntitySelector
              options={filterOptions}
              value={option?.id}
              onChange={(id) => setOption(id ? filterOptions.find(o => o.id === id) : undefined)}
              label={filterLabel}
              disabled={uniqueOption}
            />
          )}
          {!lgDown && <Divider orientation='vertical' flexItem />}
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box
            display='flex'
            alignItems='center'
            width='100%'
            height='100%'
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              gap={lgDown ? 6 : 5}
              sx={{ flex: 1 }}
            >
              <IconButton onClick={handleBackButtonClick}>
                <Icon>arrow_back</Icon>
              </IconButton>

              <Typography>{dateView}</Typography>

              <IconButton onClick={handleAdvanceButtonClick}>
                <Icon>arrow_forward</Icon>
              </IconButton>
            </Box>

            {lgDown && (
              <IconButton onClick={(e) => setAnchorElProfilePopoverNew(e.currentTarget)}>
                <Icon>add</Icon>
              </IconButton>
            )}
          </Box>
        </Grid>

        {!lgDown &&
          <Grid item xs={12} lg={2}>
            <Box display='flex' alignItems='center' height='100%'>
              <Button
                label='Novo'
                variant='contained'
                size='large'
                fullWidth
                onClick={(e) => setAnchorElProfilePopoverNew(e.currentTarget)}
              />
            </Box>
          </Grid>
        }

        <Popover
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          anchorEl={anchorElProfilePopoverNew}
          open={isPopoverNewOpen}
          onClose={handleClosePopover}
          slotProps={{
            paper: {
              sx: {
                width: anchorElProfilePopoverNew ? anchorElProfilePopoverNew.offsetWidth : 'auto',
                minWidth: 180,
              },
            },
          }}
        >
          <Box display='flex' flexDirection='column'>
            <Button
              label='Agendamento'
              onClick={onClickSchedule}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
            />

            <Button
              label='Encaixe'
              onClick={onClickWalkIn}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'success.main' }}
            />

            <Button
              label='Bloqueio'
              onClick={onClickBlocker}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
            />
          </Box>
        </Popover>
      </Grid>

      {isLoading && <LinearProgress />}

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {view.id === SchedulerViewEnum.Dia &&
          <DayView
            businessId={businessId}
            filterOptionType={filterOptionType}
            option={option}
            date={date}
            startTime={startTime}
            endTime={endTime}
            hours={generateTimes()}
            refetch={refetch}
            setIsLoading={setIsLoading}
          />
        }

        {!lgDown && view.id === SchedulerViewEnum.Semana &&
          <WeekView
            businessId={businessId}
            filterOptionType={filterOptionType}
            option={option}
            date={date}
            startTime={startTime}
            endTime={endTime}
            hours={generateTimes()}
            refetch={refetch}
            setIsLoading={setIsLoading}
          />
        }

        {!lgDown && view.id === SchedulerViewEnum.Mês &&
          <MonthView
            businessId={businessId}
            filterOptionType={filterOptionType}
            option={option}
            date={date}
            refetch={refetch}
            setIsLoading={setIsLoading}
            onDayClick={(day) => {
              setDate(day);
              setView(getSchedulerViewOptions().find(x => x.id === SchedulerViewEnum.Dia));
            }}
          />
        }
      </Box>

      {/* ── Modais ─────────────────────────────────────────────── */}

      <SchedulerCreateAppointmentModal
        open={modalType === 'appointment'}
        businessId={resolvedBusinessId}
        businessEmployeeId={resolvedBusinessEmployeeId}
        walkIn={false}
        onClose={handleCloseModal}
        refetch={handleCreateRefetch}
      />

      <SchedulerCreateAppointmentModal
        open={modalType === 'walkin'}
        businessId={resolvedBusinessId}
        businessEmployeeId={resolvedBusinessEmployeeId}
        walkIn
        onClose={handleCloseModal}
        refetch={handleCreateRefetch}
      />

      <SchedulerCreateBlockerModal
        open={modalType === 'blocker'}
        businessId={resolvedBusinessId}
        businessEmployeeId={resolvedBusinessEmployeeId || undefined}
        onClose={handleCloseModal}
        refetch={handleCreateRefetch}
      />
    </Box>
  );
};
